// Cartão de um desconto na lista: tipo, código, situação, nome, valor, onde/quem/quando e as ações.
//
// Os estilos ficam em constantes de módulo, não em `sx={{ ... }}` dentro de `cond && (...)`: o plugin de
// i18n (scripts/i18n/babel-plugin-i18n-wrap.mjs) embrulha em __t() todo literal de texto que encontra
// num `&&`/ternário de JSX, inclusive os valores de `sx` ('left', '2px solid ...'), e eles viram
// "texto a traduzir" no extrator.
import { useState, type MouseEvent } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, ListItemIcon, Menu, MenuItem, Skeleton } from '@mui/material';
import BlockIcon from '@mui/icons-material/Block';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EventIcon from '@mui/icons-material/Event';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import StoreIcon from '@mui/icons-material/Store';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useCreatePath } from 'react-admin';
import { __t } from '../../../i18n/t';
import { StatusChip, TypeBadge } from './DiscountChips';
import { DiscountStatusKey, DiscountTypeKey, dayMonthYear, getDiscountStatus, typeKey } from './discountStatus';
import { DiscountUsage, usageText } from './discountUsage';
import {
    CardNote, Lookups, NoteKind, SummaryInput, ValueSummary, audienceText, cardNotes, cardTitle, periodText, stationsText, valueSummary,
} from './discountSummary';

const INK = '#1e293b';
const MUTED = '#475569';
const focusRing = { outline: '2px solid #4433B4', outlineOffset: 2 };

const NOTE_ICON = {
    [NoteKind.info]: InfoOutlinedIcon,
    [NoteKind.lock]: LockOutlinedIcon,
    [NoteKind.history]: HistoryIcon,
    [NoteKind.warning]: WarningAmberIcon,
};

const cardSx = {
    bgcolor: '#fff', border: '1px solid rgba(120,105,223,.14)', borderRadius: '16px', boxShadow: '0 4px 24px rgba(0,0,0,.06)',
    p: { xs: '14px', sm: '16px 18px' }, display: 'flex', flexWrap: 'wrap', gap: '12px 24px', alignItems: 'flex-start',
    // O cartão inteiro abre o desconto: o link do título se estica por cima (::after) e as ações ficam acima dele.
    position: 'relative', cursor: 'pointer',
    transition: 'box-shadow .15s ease, border-color .15s ease', '&:hover': { boxShadow: '0 8px 28px rgba(30,41,59,.10)', borderColor: 'rgba(68,51,180,.35)' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
};
// 520 px de base: abaixo disso as ações descem para baixo do texto, sem estourar a largura.
const mainSx = { flex: '999 1 520px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 };
const asideSx = { flex: '1 1 270px', minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: { xs: 'stretch', md: 'flex-end' }, gap: 1.25 };
const headRowSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 };
const titleSx = { m: 0, fontSize: 16.5, fontWeight: 700, color: INK, overflowWrap: 'anywhere' };
// Alvo de toque de 40 px: o link é uma caixa de verdade (não um texto de 20 px com preenchimento).
const linkSx = {
    display: 'inline-flex', alignItems: 'center', minHeight: 40, minWidth: 40, color: INK, textDecoration: 'none', '&:focus-visible': { ...focusRing, borderRadius: '4px' },
    '&::after': { content: '""', position: 'absolute', inset: 0, borderRadius: '16px' },
    'article:hover &': { textDecoration: 'underline' },
};
const codeSx = { display: 'inline-flex', alignItems: 'center', minHeight: 40, fontSize: 13, fontWeight: 700, color: MUTED, fontVariantNumeric: 'tabular-nums' };
const valueRowSx = { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 8px' };
const headlineSx = { fontSize: 18, fontWeight: 700, color: INK, fontVariantNumeric: 'tabular-nums' };
const fuelsSx = { fontSize: 14, color: MUTED, overflowWrap: 'anywhere' };
const metaRowSx = { display: 'flex', flexWrap: 'wrap', gap: '6px 18px' };
const metaItemSx = { display: 'inline-flex', alignItems: 'flex-start', gap: '6px', fontSize: 13.5, color: MUTED, minWidth: 0 };
const metaIconSx = { fontSize: 16, color: '#64748b', flex: 'none', mt: '2px' };
const noteSx = { display: 'flex', gap: '6px', alignItems: 'flex-start', fontSize: 13, color: MUTED, lineHeight: 1.45 };
const createdSx = { fontSize: 13, color: MUTED, textAlign: { xs: 'left', md: 'right' } };
// Ações acima do link esticado do cartão: o clique em Duplicar/Desativar não abre o desconto.
// O vão entre os botões deixa o clique passar para o cartão (pointer-events), os botões não.
const actionsLayerSx = { position: 'relative', zIndex: 1, pointerEvents: 'none' };
const actionsSx = { ...actionsLayerSx, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: { xs: 'stretch', md: 'flex-end' }, '& > *': { flex: { xs: '1 1 auto', md: '0 0 auto' }, pointerEvents: 'auto' } };
const inlineSkeletonSx = { display: 'inline-block' };
const duplicateSx = {
    minHeight: 40, px: 1.5, textTransform: 'none', fontSize: 14, fontWeight: 500, color: INK, border: '1px solid #7c8595', borderRadius: '8px', bgcolor: '#fff', whiteSpace: 'nowrap',
    '&:hover': { bgcolor: '#f1f5f9', border: '1px solid #7c8595' }, '&.Mui-focusVisible, &:focus-visible': focusRing,
};
const disableSx = {
    minHeight: 40, px: 1.5, textTransform: 'none', fontSize: 14, fontWeight: 500, color: '#c62828', borderRadius: '8px',
    '&:hover': { bgcolor: 'rgba(198,40,40,.07)' }, '&.Mui-focusVisible, &:focus-visible': focusRing,
};
const menuPaperSx = { minWidth: 250, borderRadius: '12px', mt: 0.5 };
const menuItemSx = { minHeight: 44 };

const ValueLine = ({ value, loading }: { value: ValueSummary; loading: boolean }) => (
    <Box sx={valueRowSx}>
        <Box component="span" sx={headlineSx}>{value.headline}</Box>
        <Box component="span" sx={fuelsSx}>
            {loading ? <Skeleton width={110} sx={inlineSkeletonSx} /> : [value.fuels, value.extra].filter(Boolean).join(' · ')}
        </Box>
    </Box>
);

const MetaLine = ({ icon: Icon, text, loading }: { icon: typeof StoreIcon; text: string; loading: boolean }) => (
    <Box component="span" sx={metaItemSx}>
        <Icon aria-hidden="true" sx={metaIconSx} />
        <span style={{ overflowWrap: 'anywhere' }}>{loading ? <Skeleton width={120} /> : text}</span>
    </Box>
);

const NoteLine = ({ note }: { note: CardNote }) => {
    const NoteIcon = NOTE_ICON[note.kind];
    return (
        <Box component="span" sx={noteSx}>
            <NoteIcon aria-hidden="true" sx={{ fontSize: 16, color: note.kind === NoteKind.warning ? '#ed6c02' : MUTED, flex: 'none', mt: '1px' }} />
            <span>{note.text}</span>
        </Box>
    );
};

interface Props {
    record: any;
    lookups: Lookups;
    /** Abastecimentos deste desconto (GET /discount-usage); sem número, o cartão não mostra a linha. */
    usage?: DiscountUsage;
    busy: boolean;
    onDuplicate: (record: any) => void;
    onDuplicateDates: (record: any, opener: HTMLElement) => void;
    onDisable: (record: any, opener: HTMLElement) => void;
}

const DiscountCard = ({ record, lookups, usage, busy, onDuplicate, onDuplicateDates, onDisable }: Props) => {
    const createPath = useCreatePath();
    const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

    const input = record as SummaryInput;
    const status = getDiscountStatus(record);
    const value = valueSummary(input, lookups.fuels);
    const title = cardTitle(input, lookups.stations);
    const period = periodText(input);
    const notes = cardNotes(input, status, value.hasValue);
    const showUrl = createPath({ resource: 'discounts', type: 'show', id: record.id });
    const code: string = record.identify || '';
    const titleId = `discount-title-${record.id}`;
    const menuId = `discount-menu-${record.id}`;
    const duplicateLabel = __t('Duplicar %{codigo}', { codigo: code });
    const created = dayMonthYear(record.createdAt);

    // Promoção com datas ganha o menu "igual / com novas datas"; os outros tipos duplicam direto.
    const canChangeDates = typeKey(record) === DiscountTypeKey.special && Boolean(record.startAt);
    // Desativar o que já acabou, ou já está desativado, não muda nada para o cliente.
    const canDisable = status.key === DiscountStatusKey.active || status.key === DiscountStatusKey.scheduled;

    const usedText = usageText(usage, status.key);
    const meta = [
        { key: 'stations', icon: StoreIcon, text: stationsText(input, lookups.stations) },
        { key: 'audience', icon: GroupsIcon, text: audienceText(input, lookups) },
    ];

    return (
        <Box component="article" aria-labelledby={titleId} sx={cardSx}>
            <Box sx={mainSx}>
                <Box sx={headRowSx}>
                    <TypeBadge record={record} />
                    <Box component="span" sx={codeSx}>{code}</Box>
                    <StatusChip status={status} />
                </Box>

                <Box component="h2" id={titleId} sx={titleSx}>
                    <Box component={Link} to={showUrl} sx={linkSx}>{title}</Box>
                </Box>

                {value.hasValue ? <ValueLine value={value} loading={lookups.loading} /> : null}

                <Box sx={metaRowSx}>
                    {meta.map(item => <MetaLine key={item.key} icon={item.icon} text={item.text} loading={lookups.loading} />)}
                    {period ? <MetaLine icon={EventIcon} text={period} loading={false} /> : null}
                    {usedText ? <MetaLine icon={LocalGasStationIcon} text={usedText} loading={false} /> : null}
                </Box>

                {notes.map(note => <NoteLine key={note.kind} note={note} />)}
            </Box>

            <Box sx={asideSx}>
                {created ? <Box component="span" sx={createdSx}>{__t('Criado em %{dia}', { dia: created })}</Box> : null}
                <Box sx={actionsSx}>
                    {canChangeDates ? (
                        <Button
                            id={`discount-duplicate-${record.id}`}
                            aria-label={duplicateLabel}
                            aria-haspopup="menu"
                            aria-expanded={Boolean(menuAnchor)}
                            aria-controls={menuAnchor ? menuId : undefined}
                            disabled={busy}
                            onClick={(event: MouseEvent<HTMLElement>) => setMenuAnchor(event.currentTarget)}
                            startIcon={<ContentCopyIcon />}
                            endIcon={menuAnchor ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            sx={duplicateSx}
                        >
                            Duplicar
                        </Button>
                    ) : (
                        <Button
                            id={`discount-duplicate-${record.id}`}
                            aria-label={duplicateLabel}
                            disabled={busy}
                            onClick={() => onDuplicate(record)}
                            startIcon={<ContentCopyIcon />}
                            sx={duplicateSx}
                        >
                            Duplicar
                        </Button>
                    )}
                    {canDisable ? (
                        <Button
                            id={`discount-disable-${record.id}`}
                            aria-label={__t('Desativar %{codigo}', { codigo: code })}
                            onClick={(event: MouseEvent<HTMLElement>) => onDisable(record, event.currentTarget)}
                            startIcon={<BlockIcon />}
                            sx={disableSx}
                        >
                            Desativar
                        </Button>
                    ) : null}
                </Box>
                {canChangeDates ? (
                    <Menu
                        id={menuId}
                        anchorEl={menuAnchor}
                        open={Boolean(menuAnchor)}
                        onClose={() => setMenuAnchor(null)}
                        slotProps={{ paper: { sx: menuPaperSx } }}
                    >
                        <MenuItem sx={menuItemSx} onClick={() => { setMenuAnchor(null); onDuplicate(record); }}>
                            <ListItemIcon><ContentCopyIcon fontSize="small" /></ListItemIcon>
                            Duplicar igual
                        </MenuItem>
                        <MenuItem
                            sx={menuItemSx}
                            onClick={() => {
                                const opener = menuAnchor;
                                setMenuAnchor(null);
                                if (opener) onDuplicateDates(record, opener);
                            }}
                        >
                            <ListItemIcon><EventIcon fontSize="small" /></ListItemIcon>
                            Duplicar com novas datas
                        </MenuItem>
                    </Menu>
                ) : null}
            </Box>
        </Box>
    );
};

export default DiscountCard;
